// Build the document "redaction report" CSV — one row per redaction described
// by a saved `document-redaction-data` asset (VE-22300). This is the document
// analogue of the video flow's audit-log report, but instead of audit *events*
// it enumerates the redactions themselves: page, type, source text, redaction
// code, PII type, and geometry.
//
// The source of truth is the persisted asset (`DocumentRedactionAssetV1`), not
// the live editor session, so the report reflects exactly what was burned into
// the redacted file it's downloaded alongside. See `onDownloadRedactionReport`
// in sagas/exportPush.ts for the load step.

import Papa from 'papaparse';

import {
  DocumentRedactionAssetV1,
  PersistedPoint,
  PersistedRedactionEntry,
  PII_TYPE_LABELS,
} from './models';

// Column order for the table portion of the report. Confirmed with the ticket
// owner: Page | Type | Keyword/Text | Redaction Code | PII Type | X/Y/W/H, plus
// a trailing `Full Text` column. The `Keyword / Text` column shows a truncated
// preview so a long phrase doesn't blow out the row width; the untruncated text
// lives in the last column where it can't push the other columns off-screen.
const COLUMNS = [
  'Page',
  'Type',
  'Keyword / Text',
  'Redaction Code',
  'PII Type',
  'X',
  'Y',
  'Width',
  'Height',
  'Full Text',
] as const;

// Character budget for the `Keyword / Text` preview column. Longer values are
// clipped with an ellipsis; the full value is still emitted in `Full Text`.
const TEXT_PREVIEW_LIMIT = 30;

/** Box geometry in PDF points — shared by entry outlines and PII match boxes. */
interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** PII match geometry keyed by vendor match id (see `buildPiiMatchBoxes`). */
export type PiiBoxMap = Record<string, Box>;

export interface RedactionReportMeta {
  documentName?: string;
  /** Monotonic version number of the redacted file the report describes. */
  version?: number | null;
  /** ISO timestamp the redacted file was created. */
  exportedAt?: string;
}

/** A CSV cell — string for text/labels, number for coordinates, '' for empty. */
type Cell = string | number;

// CSV formula-injection guard (QA-4). Papa.unparse quotes commas and quotes
// but does NOT neutralize cells that spreadsheet apps evaluate as formulas: a
// document whose extracted text — or a hand-renamed overlay — begins with
// `=`, `+`, `-`, `@`, tab or CR would execute when a reviewer opens the report
// in Excel/Sheets. Prefixing a single quote is the standard OWASP mitigation:
// the spreadsheet renders the cell as text (the quote is not displayed).
// Leading whitespace and newlines count: LibreOffice evaluates " =cmd|..."
// just as readily as "=cmd|...", so the trigger is tested past any leading
// whitespace and a leading control character is a trigger in its own right.
const FORMULA_TRIGGER = /^\s*[=+\-@]|^[\t\r\n]/;

function sanitizeCell(cell: Cell): Cell {
  return typeof cell === 'string' && FORMULA_TRIGGER.test(cell)
    ? `'${cell}`
    : cell;
}

// Clip a value to the preview budget, appending an ellipsis when truncated.
function preview(text: string): string {
  return text.length > TEXT_PREVIEW_LIMIT
    ? `${text.slice(0, TEXT_PREVIEW_LIMIT)}...`
    : text;
}

// Assemble one table row in column order. `fullText` drives both the truncated
// preview column and the trailing full-text column. Every string cell passes
// the injection guard — the text columns and the user-defined labels
// (redaction code, lexicon name) are all document- or user-controlled.
function makeRow(
  pageLabel: string,
  type: string,
  fullText: string,
  code: string,
  piiType: string,
  box: Box | null
): Cell[] {
  return [
    pageLabel,
    type,
    preview(fullText),
    code,
    piiType,
    ...coordCells(box),
    fullText,
  ].map(sanitizeCell);
}

// Round coordinates to 2 decimals so the CSV stays readable; the burn-in
// geometry is in PDF points where sub-point precision isn't meaningful here.
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Axis-aligned bounding box of an outline (the persisted entry's `points`). */
function pointsToBox(points: PersistedPoint[]): Box | null {
  if (!Array.isArray(points) || points.length === 0) {
    return null;
  }
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

// Coordinate cells for an entry — four cells, blank when geometry is absent
// (page-level flags, or a PII match whose vendor id wasn't in the result).
function coordCells(box: Box | null): [Cell, Cell, Cell, Cell] {
  if (!box) {
    return ['', '', '', ''];
  }
  return [round2(box.x), round2(box.y), round2(box.width), round2(box.height)];
}

// Human label for a drawn/keyword/text-select entry's `Type` column.
function entryTypeLabel(
  origin: 'manual' | 'keyword' | 'text-select',
  entry: PersistedRedactionEntry
): string {
  if (origin === 'keyword') {
    return 'Keyword';
  }
  if (origin === 'text-select') {
    return 'Text Select';
  }
  // `manual` covers both rectangles and custom polygons (the latter carry
  // `isCustomPolygon`). Split them so the report distinguishes the shape.
  return entry.isCustomPolygon ? 'Polygon' : 'Manual';
}

function entryRow(
  pageLabel: string,
  origin: 'manual' | 'keyword' | 'text-select',
  entry: PersistedRedactionEntry
): Cell[] {
  // Keyword / text-select carry the source `phrase`; drawn regions carry a
  // user-editable `name` (e.g. "Overlay 3"). Surface whichever applies.
  const text =
    origin === 'manual'
      ? (entry.name ?? '')
      : (entry.phrase ?? entry.name ?? '');
  return makeRow(
    pageLabel,
    entryTypeLabel(origin, entry),
    text,
    entry.redactionCode?.label ?? '',
    '',
    pointsToBox(entry.points)
  );
}

// Numeric sort of the page-bucket keys, with the "all" bucket (stick-to-all-
// pages redactions) sorted last.
function comparePageKeys(a: string, b: string): number {
  if (a === 'all') {
    return 1;
  }
  if (b === 'all') {
    return -1;
  }
  return Number(a) - Number(b);
}

/**
 * Format a `document-redaction-data` asset body into the redaction-report CSV.
 *
 * Only redactions that are actually applied are included: entries with
 * `redacted === false` (un-redacted but kept in the list) and PII matches that
 * are un-redacted or deleted are skipped — matching the "active redactions +
 * PII" scope agreed for the report.
 *
 * `piiBoxes` maps a PII match's vendor id to its PDF-point box (from the
 * vtn-standard result; see `buildPiiMatchBoxes`). When omitted, PII rows have
 * blank coordinates — the asset itself doesn't store PII geometry.
 */
export function formatDocumentRedactionReportCsv(
  body: DocumentRedactionAssetV1,
  meta: RedactionReportMeta = {},
  piiBoxes: PiiBoxMap = {}
): string {
  const dataRows: Cell[][] = [];

  // Lexicon cards live at the asset root; their matches are bucketed per page.
  const lexiconCards = new Map(
    (body.lexiconDetections ?? []).map((card) => [card.id, card] as const)
  );

  const pageKeys = Object.keys(body.redactions ?? {}).sort(comparePageKeys);
  for (const pageKey of pageKeys) {
    const bucket = body.redactions[pageKey];
    if (!bucket) {
      continue;
    }
    const pageLabel = pageKey === 'all' ? 'All' : pageKey;

    for (const origin of ['manual', 'keyword', 'text-select'] as const) {
      const entries = bucket[origin];
      if (!entries) {
        continue;
      }
      for (const entry of Object.values(entries)) {
        // `redacted` is only persisted when false (un-redacted); default =
        // burned in. Skip the un-redacted ones.
        if (entry.redacted === false) {
          continue;
        }
        dataRows.push(entryRow(pageLabel, origin, entry));
      }
    }

    // PII matches added on this page. Presence = added to the Redactions list;
    // `redacted` = burned in. Geometry isn't stored on the asset — it's looked
    // up from the vtn-standard result via `piiBoxes` (keyed by vendor match id).
    for (const match of bucket.PII ?? []) {
      if (!match.redacted || match.deleted) {
        continue;
      }
      dataRows.push(
        makeRow(
          pageLabel,
          'PII',
          match.text ?? '',
          match.redactionCode?.label ?? '',
          PII_TYPE_LABELS[match.piiType] ?? match.piiType,
          piiBoxes[match.id] ?? null
        )
      );
    }

    // Lexicon matches on this page (VE-24948 / U5) — redacted ones only, like
    // PII. One row per stored outline (a wrapped match has one per text row),
    // so the geometry columns are exact. The category column carries the
    // LEXICON NAME and the text columns stay blank, because NFR-S7 forbids
    // putting a term into this report — which everyone with document access
    // can read, a wider audience than the asset has.
    //
    // Note the reason is the AUDIENCE, not the absence of the term: the term
    // IS on the asset (`buildLexiconAssetRecords` writes `card.label` and a
    // `snapshot` containing `term`, as U5-FR2 requires). An earlier version of
    // this comment claimed otherwise, which would mislead the next person
    // deciding where a term may go (review-gate vsec nit).
    for (const match of bucket.LEXICON ?? []) {
      if (!match.redacted || match.deleted) {
        continue;
      }
      const card = lexiconCards.get(match.detectionId);
      const category = card
        ? `Lexicon: ${card.snapshot.lexiconName}`
        : 'Lexicon';
      for (const outline of match.rects) {
        dataRows.push(
          makeRow(
            pageLabel,
            'Lexicon',
            '',
            card?.redactionCode?.label ?? '',
            category,
            pointsToBox(outline)
          )
        );
      }
    }

    // Page-level treatments. Not box redactions, but materially change the
    // output, so the audit report lists them with blank geometry.
    if (bucket.pageContentRemoved || bucket.pageFullyRedacted) {
      dataRows.push(makeRow(pageLabel, 'Page Removed', '', '', '', null));
    }
    if (bucket.pageHidden || bucket.pageRemoved) {
      dataRows.push(makeRow(pageLabel, 'Page Hidden', '', '', '', null));
    }
  }

  // Leading metadata block, then a blank separator row, then the table. The
  // document name is user-controlled, so it passes the injection guard too.
  const metaRows: Cell[][] = [
    ['Document', sanitizeCell(meta.documentName ?? '')],
    ['Version', meta.version ?? ''],
    ['Exported', meta.exportedAt ?? ''],
    ['Total Redactions', dataRows.length],
    [],
  ];

  return Papa.unparse([...metaRows, [...COLUMNS], ...dataRows]);
}
